import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Pencil, AlertTriangle, X } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatRange, formatDate } from "@/lib/utils_gg";
import StatusBadge from "@/components/StatusBadge";
import FeaturedControls from "@/components/FeaturedControls";
import { toast } from "sonner";

const Row = ({ label, value, testid }) => value ? (
  <div className="grid grid-cols-[120px_1fr] gap-3 py-2 border-b border-foreground/30 text-sm" data-testid={testid}><div className="eyebrow text-muted-foreground">{label}</div><div className="break-words">{value}</div></div>
) : null;

function Dialog({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose} data-testid={testid}>
      <div onClick={e => e.stopPropagation()} className="gg-card bg-background p-5 w-full sm:max-w-lg">
        <div className="flex items-center justify-between"><h3 className="font-display uppercase text-2xl">{title}</h3><button onClick={onClose} className="gg-icon-btn"><X className="w-5 h-5" /></button></div>
        <div className="mt-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

export default function AdminEventReview() {
  const { event_id } = useParams();
  const { user, t, lang, checkingAuth } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(null); // approve | request_changes | reject | delete | organizer
  const [comment, setComment] = useState("");
  const [org, setOrg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/events/${event_id}`).then(r => setD(r.data)).catch(() => setD(false));
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-line */ }, [event_id, user?.role]);
  if (checkingAuth) return null;
  if (!user) return <Navigate to="/auth" />;
  if (user.role !== "admin") return <div className="p-8">403 — Admin only.</div>;
  if (d === false) return <div className="p-8 eyebrow">{t("notFound")}</div>;
  if (!d) return null;
  const e = d.event;

  const act = async (action, extra = {}) => {
    if ((action === "request_changes" || action === "reject") && !comment.trim()) { toast.error(t("commentRequired")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${event_id}/action`, { action, comment, ...extra });
      if (data.deleted) { toast.success("Deleted"); nav("/admin"); return; }
      toast.success(action === "approve" ? t("approvedToast") : "Done");
      setDlg(null); setComment(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const openOrg = async () => { const { data } = await api.get(`/admin/organizers/${e.organizer_id}`); setOrg(data); setDlg("organizer"); };
  const money = e.free_event ? t("freeEvent") : e.price != null ? `${e.currency} ${e.price}` : "—";
  const fieldLabel = k => ({ name: t("eventName"), start_date: t("startDate"), end_date: t("endDate"), start_time: t("startTime"), end_time: t("endTime"), venue: t("venue"), address: t("address"), city: t("city"), country: t("country"), genres: t("genre"), event_type: t("eventType") }[k] || k);
  const fmt = v => Array.isArray(v) ? v.join(", ") : v == null || v === "" ? "—" : String(v);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 pb-40 md:pb-32" data-testid="admin-event-review">
      <Link to="/admin" className="eyebrow inline-flex items-center gap-1 hover:underline" data-testid="review-back"><ArrowLeft className="w-3 h-3" />{t("admin")}</Link>
      <div className="mt-3 flex items-start justify-between gap-3 flex-wrap border-b border-foreground pb-3">
        <div><div className="eyebrow text-primary">{t("eventReview")}</div>
          <h1 className="font-display uppercase text-3xl md:text-5xl tracking-tighter leading-none" data-testid="review-title">{e.name}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap"><StatusBadge status={e.mod_status} />
            {e.public_status && <span className="gg-chip !text-[10px]">{t(`public.${e.public_status}`)}</span>}
            {e.has_pending_changes && <span className="gg-chip !text-[10px] border-primary text-primary" data-testid="badge-changes-pending">{t("changesPending")}</span>}
            <span className="text-xs text-muted-foreground">ID {e.event_id}</span></div></div>
        <div className="flex gap-2">
          <Link to={`/events/${e.slug}`} target="_blank" className="gg-btn !py-2 !px-3 text-xs" data-testid="btn-preview"><ExternalLink className="w-4 h-4" />{t("previewEvent")}</Link>
          <Link to={`/admin/events/${e.event_id}/edit`} className="gg-btn !py-2 !px-3 text-xs" data-testid="btn-edit"><Pencil className="w-4 h-4" />{t("editEventBtn")}</Link>
        </div>
      </div>

      {d.duplicates.length > 0 && (
        <div className="mt-5 p-4 border border-destructive" data-testid="duplicate-warning">
          <div className="eyebrow text-destructive flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{t("possibleDuplicate")}</div>
          <ul className="mt-2 space-y-1 text-sm">
            {d.duplicates.map(x => (
              <li key={x.event_id} className="flex flex-wrap gap-x-3 items-center">
                <Link to={`/admin/events/${x.event_id}`} className="underline font-display uppercase">{x.name}</Link>
                <span>{formatRange(x, lang)} · {x.venue} · {x.organizer_name}</span>
                <span className="text-xs text-muted-foreground">{t("similarity")}: {x.reasons.join(", ")}</span>
                <StatusBadge status={x.mod_status} />
              </li>))}
          </ul>
        </div>
      )}

      {e.changes_diff && (
        <div className="mt-5 border border-primary" data-testid="changes-diff">
          <div className="p-3 bg-primary text-primary-foreground eyebrow">{t("changesPending")} · {formatDate((d.event.pending_changes_at || "").slice(0, 10), lang)}</div>
          {d.event.pending_changes_comment && <div className="p-3 border-b border-primary text-sm"><span className="eyebrow mr-2">{t("adminCommentLabel")}</span>{d.event.pending_changes_comment}</div>}
          <table className="w-full text-sm"><thead className="eyebrow text-left bg-muted/40"><tr><th className="p-2">Field</th><th className="p-2">{t("currentVersion")}</th><th className="p-2">{t("proposedVersion")}</th></tr></thead>
            <tbody className="divide-y divide-foreground/30">{Object.entries(e.changes_diff).map(([k, v]) => (
              <tr key={k} data-testid={`diff-${k}`}><td className="p-2 eyebrow">{fieldLabel(k)}</td><td className="p-2 line-through text-muted-foreground">{fmt(v.from)}</td><td className="p-2 font-medium bg-primary/10">{fmt(v.to)}</td></tr>))}</tbody></table>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-[280px_1fr] gap-6">
        <div>
          <div className="border border-foreground bg-muted aspect-[3/4] overflow-hidden"><img src={e.main_image} alt="" className="w-full h-full object-contain" data-testid="review-flyer" /></div>
          {e.additional_images?.length > 0 && <div className="mt-2 grid grid-cols-3 gap-1">{e.additional_images.map(u => <img key={u} src={u} alt="" className="aspect-square object-cover border border-foreground" />)}</div>}
          <div className="mt-4 gg-card p-3">
            <div className="eyebrow">{t("organizerCol")}</div>
            <button onClick={openOrg} className="font-display uppercase text-lg hover:text-primary text-left" data-testid="review-organizer">{e.organizer_name} →</button>
          </div>
          {e.mod_comment && <div className="mt-3 p-3 border border-foreground text-sm"><div className="eyebrow">{t("adminCommentLabel")}</div>{e.mod_comment}</div>}
        </div>
        <div data-testid="review-fields">
          <Row label={t("eventType")} value={t(`types.${e.event_type}`)} />
          <Row label={t("genre")} value={e.genres.map(g => t(`genres.${g}`)).join(", ")} />
          <Row label={t("startDate")} value={`${formatDate(e.start_date, lang)} ${e.start_time || ""}`} />
          <Row label={t("endDate")} value={`${formatDate(e.end_date, lang)} ${e.end_time || ""}`} />
          <Row label={t("venue")} value={e.venue} /><Row label={t("address")} value={e.address} />
          <Row label={t("city")} value={`${e.city}, ${e.country}`} />
          <Row label={t("djs")} value={e.djs?.join(", ")} /><Row label={t("artists")} value={e.artists?.join(", ")} /><Row label={t("liveActs")} value={e.live_acts?.join(", ")} />
          <Row label={t("shortDescription")} value={e.short_description} /><Row label={t("longDescription")} value={<span className="whitespace-pre-wrap">{e.long_description}</span>} />
          <Row label={t("musicStyle")} value={e.music_style_note} />
          <Row label={t("price")} value={money} /><Row label={t("ticketUrl")} value={e.ticket_url} /><Row label={t("eventWebsite")} value={e.event_website} />
          <Row label={t("facebookEvent")} value={e.facebook_event} /><Row label={t("instagram")} value={e.instagram} /><Row label={t("organizerWebsite")} value={e.organizer_website} />
          <Row label={t("hotelNote")} value={e.hotel_note} />
          <Row label={t("travelNote")} value={e.travel_note} />
          <Row label={t("submitted")} value={formatDate((e.submitted_at || e.created_at || "").slice(0, 10), lang)} />
          {e.approved_at && <Row label={t("approved")} value={formatDate(e.approved_at.slice(0, 10), lang)} />}
        </div>
      </div>

      {e.mod_status === "approved" && (
        <div className="mt-8 border-t border-foreground pt-5" data-testid="published-management">
          <div className="eyebrow text-primary mb-3">{t("publicStatus")}</div>
          <div className="flex flex-wrap gap-2">
            {["scheduled", "postponed", "cancelled"].map(ps => (
              <button key={ps} disabled={busy} onClick={() => act("set_public_status", { public_status: ps })} className={`gg-btn !py-2 !px-3 text-xs ${e.public_status === ps ? "!bg-foreground !text-background" : ""}`} data-testid={`ps-${ps}`}>{t(`public.${ps}`)}</button>))}
            <button disabled={busy || !["scheduled", "sold_out"].includes(e.public_status || "scheduled")} onClick={() => act("set_public_status", { public_status: e.public_status === "sold_out" ? "scheduled" : "sold_out" })} className={`gg-btn !py-2 !px-3 text-xs disabled:opacity-40 ${e.public_status === "sold_out" ? "!bg-foreground !text-background" : ""}`} data-testid="ps-sold_out">{t(e.public_status === "sold_out" ? "removeSoldOut" : "markSoldOut")}</button>
            <button disabled={busy} onClick={() => act("set_public_status", { public_status: "archived" })} className="gg-btn !py-2 !px-3 text-xs" data-testid="ps-archived">{t("archiveEvent")}</button>
            <button disabled={busy} onClick={() => act("unpublish")} className="gg-btn !py-2 !px-3 text-xs" data-testid="btn-unpublish">{t("unpublish")}</button>
          </div>
          <FeaturedControls event={e} onChange={() => load()} />
        </div>
      )}

      <div className="mt-8 border-t border-foreground pt-5" data-testid="mod-history">
        <div className="eyebrow text-primary mb-3">{t("history")}</div>
        {!d.history.length && <div className="text-sm text-muted-foreground">{t("noHistory")}</div>}
        <ul className="divide-y divide-foreground/30 text-sm">
          {d.history.map(h => (
            <li key={h.log_id} className="py-2 flex flex-wrap gap-x-3 gap-y-0.5">
              <span className="text-xs text-muted-foreground whitespace-nowrap">{h.created_at.slice(0, 16).replace("T", " ")}</span>
              <span className="font-display uppercase">{h.action.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground">{h.actor_name || h.actor_id}</span>
              {h.prev_status !== h.new_status && h.new_status && <span className="text-xs">{h.prev_status || "—"} → {h.new_status}</span>}
              {h.comment && <span className="w-full text-xs italic">“{h.comment}”</span>}
            </li>))}
        </ul>
        <button onClick={() => setDlg("delete")} className="mt-6 eyebrow text-destructive underline" data-testid="btn-delete">{t("deleteEvent")}</button>
      </div>

      {/* Sticky moderation actions */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-background border-t border-foreground" data-testid="mod-actions">
        <div className="max-w-5xl mx-auto p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          <button disabled={busy} onClick={() => setDlg("approve")} className="gg-btn-primary" data-testid="act-approve">{e.has_pending_changes ? t("approveChanges") : t("approve")}</button>
          <button disabled={busy} onClick={() => setDlg("request_changes")} className="gg-btn" data-testid="act-request">{t("requestChanges")}</button>
          <button disabled={busy} onClick={() => setDlg("reject")} className="gg-btn" data-testid="act-reject">{t("reject")}</button>
          <Link to={`/admin/events/${e.event_id}/edit`} className="gg-btn" data-testid="act-edit"><Pencil className="w-4 h-4" />{t("editEventBtn")}</Link>
        </div>
      </div>

      {dlg === "approve" && (
        <Dialog title={t("approveConfirmTitle")} onClose={() => setDlg(null)} testid="dlg-approve">
          <p className="text-sm">{t("approveConfirmBody")}</p>
          <textarea rows={2} className="gg-input" placeholder={t("moderationComment")} value={comment} onChange={ev => setComment(ev.target.value)} data-testid="dlg-comment" />
          <button disabled={busy} onClick={() => act("approve")} className="gg-btn-primary w-full" data-testid="dlg-confirm">{t("approve")}</button>
        </Dialog>
      )}
      {dlg === "request_changes" && (
        <Dialog title={t("requestChanges")} onClose={() => setDlg(null)} testid="dlg-request">
          <label className="eyebrow">{t("messageToOrganizer")} *</label>
          <textarea rows={4} required className="gg-input" value={comment} onChange={ev => setComment(ev.target.value)} data-testid="dlg-comment" />
          <p className="text-xs text-muted-foreground">{t("requestChangesHint")}</p>
          <button disabled={busy} onClick={() => act("request_changes")} className="gg-btn-primary w-full" data-testid="dlg-confirm">{t("requestChanges")}</button>
        </Dialog>
      )}
      {dlg === "reject" && (
        <Dialog title={t("reject")} onClose={() => setDlg(null)} testid="dlg-reject">
          <label className="eyebrow">{t("rejectReason")} *</label>
          <textarea rows={4} required className="gg-input" value={comment} onChange={ev => setComment(ev.target.value)} data-testid="dlg-comment" />
          <p className="text-xs text-muted-foreground">{t("rejectHint")}</p>
          <button disabled={busy} onClick={() => act("reject")} className="gg-btn w-full border-destructive text-destructive" data-testid="dlg-confirm">{t("reject")}</button>
        </Dialog>
      )}
      {dlg === "delete" && (
        <Dialog title={t("deleteEvent")} onClose={() => setDlg(null)} testid="dlg-delete">
          <p className="text-sm">{t("deleteConfirm")}</p>
          <button disabled={busy} onClick={() => act("delete")} className="gg-btn w-full border-destructive text-destructive" data-testid="dlg-confirm">{t("deleteEvent")}</button>
        </Dialog>
      )}
      {dlg === "organizer" && org && (
        <Dialog title={t("organizerInfo")} onClose={() => setDlg(null)} testid="dlg-organizer">
          <div className="eyebrow text-destructive">{t("adminOnly")}</div>
          <Row label={t("yourName")} value={org.user.name} /><Row label={t("email")} value={org.user.email} />
          <Row label={t("accountCreated")} value={formatDate((org.user.created_at || "").slice(0, 10), lang)} />
          <Row label={t("submittedEvents")} value={String(org.counts.total)} />
          <Row label={t("approved")} value={String(org.counts.approved)} /><Row label={t("rejected")} value={String(org.counts.rejected)} />
          <Row label={t("changesRequested")} value={String(org.counts.changes_requested)} /><Row label={t("pending")} value={String(org.counts.pending)} />
          <div className="flex gap-2 pt-2"><Link to={`/organizers/${org.user.user_id}`} className="gg-btn !py-2 text-xs">{t("organizer")}</Link>
            <Link to={`/admin?status=all`} onClick={() => setDlg(null)} className="gg-btn !py-2 text-xs">{t("allEvents")}</Link></div>
        </Dialog>
      )}
    </div>
  );
}
